import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CompanyDto } from '@jaam/commons/dtos';
import { AppErrorResponse, MutationParams, useAppMutation } from '@jaam/web-core';
import CompanyServicesAPI from '../services/api/company/company.service';
import { InvitationsArgs } from '../interfaces/company.interface';

interface UseCompaniesParams<T> {
  id?: number;
  onSuccess?: (data: T) => void;
  onError?: (error: AppErrorResponse) => void;
}

interface UseFetchCompaniesParams extends UseCompaniesParams<CompanyDto[]> {
  searchTerm?: string;
  page?: number;
  limit?: number;
  isAuthenticated?: boolean;
}

interface UseFetchCompanyDetailsParams extends UseCompaniesParams<CompanyDto> {
  companyId?: number;
}

export const useFetchCompanies = ({
  searchTerm,
  page = 1,
  limit = 10,
  isAuthenticated,
  onSuccess,
  onError,
}: UseFetchCompaniesParams) => {
  const {
    data: companies,
    isLoading: isFetchingCompanies,
    error: companiesError,
    refetch,
  } = useQuery<CompanyDto[], AppErrorResponse>({
    queryKey: ['companies', { searchTerm, page, limit }],
    queryFn: async () => {
      const { data } = await CompanyServicesAPI.list();
      return data as CompanyDto[];
    },
    enabled: isAuthenticated,
  });

  if (companies && onSuccess) onSuccess(companies);
  if (companiesError && onError) onError(companiesError);

  return { companies, isFetchingCompanies, companiesError, refetch };
};

export const useFetchCompanyDetails = ({
  companyId,
  onSuccess,
  onError,
}: UseFetchCompanyDetailsParams) => {
  const {
    data: company,
    isLoading: isFetchingCompany,
    error: companyError,
  } = useQuery<CompanyDto, AppErrorResponse>({
    queryKey: ['company', companyId],
    queryFn: async () => {
      const { data } = await CompanyServicesAPI.getOne(companyId!);
      return data as CompanyDto;
    },
    enabled: !!companyId,
    retry: 1,
  });

  if (company && onSuccess) onSuccess(company);
  if (companyError && onError) onError(companyError);

  return { company, isFetchingCompany, companyError };
};

export const useCreateCompany = ({
  onSuccess,
  onError,
  setError,
}: MutationParams = {}) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync: createCompany,
    isPending: isCreatingCompany,
    error: createCompanyError,
  } = useAppMutation<CompanyDto, Omit<CompanyDto, 'id'>>({
    mutationFn: async (company) => {
      const { data } = await CompanyServicesAPI.add(company);
      return data as CompanyDto;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      onSuccess?.(data);
    },
    onError: (error: AppErrorResponse) => {
      onError?.(error);
    },
    setError,
  });

  return { createCompany, isCreatingCompany, createCompanyError };
};

export const useUpdateCompany = ({
  id,
  onSuccess,
  onError,
  setError,
}: MutationParams & { id: number }) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync: updateCompany,
    isPending: isUpdatingCompany,
    error: updateCompanyError,
  } = useAppMutation<CompanyDto, Partial<CompanyDto>>({
    mutationFn: async (company) => {
      const { data } = await CompanyServicesAPI.update(id || 0, company);
      return data as CompanyDto;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['company', id] });
      onSuccess?.(data);
    },
    onError: (error: AppErrorResponse) => {
      onError?.(error);
    },
    setError,
  });

  return { updateCompany, isUpdatingCompany, updateCompanyError };
};

export const useDeleteCompany = ({
  onSuccess,
  onError,
}: UseCompaniesParams<void>) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync: deleteCompany,
    isPending: isDeletingCompany,
    error: deleteCompanyError,
  } = useMutation<void, AppErrorResponse, number>({
    mutationFn: async (id) => {
      await CompanyServicesAPI.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      onSuccess?.();
    },
    onError: (error: AppErrorResponse) => {
      onError?.(error);
    },
  });

  return { deleteCompany, isDeletingCompany, deleteCompanyError };
};

export const useInviteCompany = ({
  onSuccess,
  onError,
}: UseCompaniesParams<CompanyDto>) => {
  const {
    mutateAsync: inviteCompany,
    isPending: isInvitingCompany,
    error: inviteCompanyError,
  } = useMutation<void, AppErrorResponse, InvitationsArgs>({
    mutationFn: async ({ companyId, invitations }) => {
      await CompanyServicesAPI.inviteCompany(companyId, invitations);
    },

    onError: (error: AppErrorResponse) => {
      onError?.(error);
    },
  });

  return { inviteCompany, isInvitingCompany, inviteCompanyError };
};
